import { h, render, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text, Localizer, IntlProvider } from 'preact-i18n';
import Markdown from 'preact-markdown';
import { route } from 'preact-router';

import localUtil from '../../lib/util';
import { withoutUnpaidReservations } from '../../lib/raceSignup';

import RaceList from '../../components/race/list';
import RaceDetail from '../../components/race/detail';
import RaceClassContestantList from '../../components/raceClassContestant/list';
import RaceClassContestantResults from '../../components/race/resultsList';
import RaceResultsPrint from '../../components/race/resultsPrint';
import GeofenceStateList from '../../components/race/geofenceStateList';

import definitionNo from '../../languages/no.json';
import definitionEn from '../../languages/en.json';

const countryMap = {
    no: definitionNo,
    en: definitionEn,
    default: definitionNo,
};

@observer
class RaceResults extends Component {
  	constructor(props) {
        super(props);
        this.state = {
            sessionid: new Date().getTime(),
            autoUpdate: true,
            autoUpdateCountdown: 60,
        };
        this.mainContainer = null;
        this.countdownIntervalId = null;
    }

    async loadRaceClassContestants(props = this.props) {
		const { raceStore, raceClassStore, raceClassContestantStore, userStore } = props.stores;
        const { id, raceClassId } = props;

        const { isAdmin } = userStore;
        const { isRaceAdmin, isTimeTracker } = raceStore;
        const isWorkingWithResults = isAdmin || isRaceAdmin || isTimeTracker;
        const { showOfflineResults, showOnlyAutoResults } = raceClassContestantStore;

		await raceStore.load(id, false, {
            url: isWorkingWithResults ? null : '/api/races/public/',
        });
		await raceClassStore.load({
            url: isWorkingWithResults ? null : '/api/raceclasses/public/',
            query: {
                id: raceClassId,
                race: id,
            },
        });
        const { race } = raceStore;
// console.log({ raceClassId }, this.props);

        // Use authenticated endpoint with includeOffline/onlyAutoResults if admin/race admin has toggle on
        if ((showOfflineResults || showOnlyAutoResults) && isWorkingWithResults) {
            const query = {
                race: race.id,
                raceClass: raceClassId,
                addResults: true,
            };
            if (showOfflineResults) {
                query.includeOffline = '1';
            }
            if (showOnlyAutoResults) {
                query.onlyAutoResults = '1';
            }
            await raceClassContestantStore.loadWithResults({ query });
        } else {
            await raceClassContestantStore.load({
                query: {
                    race: race.id,
                    raceClass: raceClassId,
                    addResults: true,
                },
                addData: [],
                url: isWorkingWithResults ? null : '/api/raceclasscontestants/public/',
            });
        }
        raceClassContestantStore.getCountries();

        const { user } = userStore;
        raceStore.checkAllPositions(user);
        this.setState({
            showResults: race.showResults,
        });
    }

    loadAll = (props = this.props) => {
        const { autoUpdate } = this.state;
        if (!autoUpdate) {
            return;
        }
        const { raceStore } = props.stores;
        raceStore.deleteReloadTimer();
        this.loadRaceClassContestants(props);

        // Reset countdown
        this.setState({ autoUpdateCountdown: 60 });

        // Start countdown interval
        if (this.countdownIntervalId) {
            clearInterval(this.countdownIntervalId);
        }
        this.countdownIntervalId = setInterval(() => {
            this.setState(prevState => ({
                autoUpdateCountdown: Math.max(0, prevState.autoUpdateCountdown - 1)
            }));
        }, 1000);

        raceStore.setReloadTimer(setTimeout(() => {
            this.loadAll(props);
        }, 1000 * 60));
    }

    stopTimer = () => {
        const { raceStore } = this.props.stores;
        raceStore.deleteReloadTimer();
        // Stop countdown interval
        if (this.countdownIntervalId) {
            clearInterval(this.countdownIntervalId);
            this.countdownIntervalId = null;
        }
    }

    toggleAutoUpdate = () => {
        const { autoUpdate } = this.state;
        const newAutoUpdate = !autoUpdate;

        if (newAutoUpdate) {
            // Start auto-refresh
            this.setState({ autoUpdate: newAutoUpdate }, () => {
                this.loadAll();
            });
        } else {
            // Stop auto-refresh
            this.stopTimer();
            this.setState({ autoUpdate: newAutoUpdate });
        }
    }

    toggleView = () => {
        this.setState({
            showResults: !this.state.showResults,
        });
    }

    onClickPrintResults = () => {
        const { userStore } = this.props.stores;
        const { user } = userStore;
        const { language = 'no' } = user;
        const languageDef = countryMap[language || 'default'];

        const printComponent = (
            <IntlProvider definition={languageDef}>
                <RaceResultsPrint stores={this.props.stores} />
            </IntlProvider>
        );

        const printDiv = document.createElement('div');
        printDiv.className = 'raceResultsPrint';
        document.body.appendChild(printDiv);
        render(printComponent, printDiv);

        setTimeout(() => {
            window.print();
            document.body.removeChild(printDiv);
            render(null, printDiv);
        }, 100);
    }

    componentDidMount() {
        // console.log('RaceResults.componentDidMount');
        this.loadAll();
        const { raceClassId } = this.props;
        const { appState, raceStore, raceClassStore } = this.props.stores;
        raceStore.addRefreshPageFunction(() => this.loadAll(), 'raceResults');
        const { race } = raceStore;
        appState.trackRace('result_view', race);
        // Track raceClass result view if specific raceClass is selected
        if (raceClassId) {
            const { raceClasses } = raceClassStore;
            const raceClass = raceClasses?.find(rc => rc.id === parseInt(raceClassId, 10));
            if (raceClass) {
                appState.trackRaceClass('result_view', raceClass);
            }
        }
    }

    componentWillReceiveProps(nextProps) {
        if (nextProps.id !== this.props.id || nextProps.raceClassId !== this.props.raceClassId) {
            this.loadAll(nextProps);
        }
    }

    componentWillUnmount() {
        const { raceStore } = this.props.stores;
        raceStore.removeRefreshPageFunction('raceResults');
        raceStore.deleteReloadTimer();
        // Clean up countdown interval
        if (this.countdownIntervalId) {
            clearInterval(this.countdownIntervalId);
            this.countdownIntervalId = null;
        }
    }

    render() {
        const { id, raceClassId, drawerLevel } = this.props;
        const { userStore, raceStore, raceClassStore, raceClassContestantStore } = this.props.stores;
        const { user = {}, isAdmin, isTester, isVeterinary } = userStore;
        const {  isRaceAdmin } = raceStore;
        const darkmode= util.getNestedValue(user, 'settings.darkmode');
        const { race } = raceStore;
        const { raceClasses } = raceClassStore;
        const { raceClassContestants: allRaceClassContestants, countries } = raceClassContestantStore;
        // Unpaid forced-payment reservations hold a slot but are not public
        // signups yet - hide them from both the Results and Signups tabs.
        const raceClassContestants = withoutUnpaidReservations(allRaceClassContestants);
        const { showResults = race.showResults, autoUpdate, autoUpdateCountdown } = this.state;

        const finalRaceClasses = raceClasses.filter(rc => !rc.title.includes('⚠️'));


        return (<>
            <div class='w-100 d-flex justify-content-center'>
                <div class='w-100 h-100'>
                    {/* Not logged in race results here... */}
                    {!race.showResults && isRaceAdmin && <>
                        <div class='w-100 d-flex flex-row justify-content-center mt-1 mb-2'>
                            <button type='button' class={`btn btn-sm btn${showResults ? '' : '-outline'}-danger rounded-pill-left`} onClick={this.toggleView}>
                                Results
                            </button>
                            <button type='button' class={`btn btn-sm btn${!showResults ? '' : '-outline'}-danger rounded-pill-right`} onClick={this.toggleView}>
                                Signups
                            </button>
                        </div>
                    </>}

                    {showResults && finalRaceClasses?.length > 0 && raceClassContestants?.length > 0 && (
                        <div class='w-100 d-flex flex-row justify-content-end px-2 mb-2'>
                            <button
                                type='button'
                                class='btn btn-sm btn-outline-secondary rounded-pill'
                                onClick={this.onClickPrintResults}
                            >
                                <i class='fa-solid fa-print me-1' />
                                <Text id='race.print-results'>Skriv ut resultater</Text>
                            </button>
                        </div>
                    )}

                    {showResults ? <>
                        {(finalRaceClasses && finalRaceClasses.length > 0 && raceClassContestants && raceClassContestants.length > 0) ? <>
                            {finalRaceClasses.map((raceClass, idx) => {
                                const hasContestants = raceClassContestants.filter(rcc => rcc.raceClass === raceClass.id).length > 0;
                                if (!hasContestants) {
                                    return null;
                                }

                                return (<>
                                    <RaceClassContestantResults
                                        stores={this.props.stores}
                                        classNames={idx > 0 ? 'mt-5' : ''}
                                        title={`${raceClass.title} (${raceClassContestants.filter(rcc => rcc.raceClass === raceClass.id).length})`}
                                        race={race}
                                        raceClass={raceClass}
                                        raceClassId={raceClassId}
                                        raceClassContestants={raceClassContestants.filter(rcc => rcc.raceClass === raceClass.id)}
                                        // hideEmpty={}
                                        showCevron={false}
                                        key={`raceClassContestantResults-${raceClass.id}`}
                                        drawerLevel={drawerLevel}
                                        stopTimer={this.stopTimer}
                                        autoUpdate={autoUpdate}
                                        autoUpdateCountdown={autoUpdateCountdown}
                                        toggleAutoUpdate={this.toggleAutoUpdate}
                                    />
                                </>);
                            })}
                        </> : <>
                            <div class={`d-flex flex-column mx-3 rounded-lg ${darkmode ? 'bg-darkmode' : 'bg-lightmode'} px-4 py-3`}>
                                <div class='w-100 d-flex flex-column px-4 py-3 box-line'>
                                    <div class='w-100 d-flex flex-row justify-content-center'>
                                        <div class='box-line-add'>
                                            <i class='fa-solid fa-circle-question' />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </>}

                    </> : <>
                        {(raceClasses && raceClasses.length > 0) ? <>
                            {raceClasses.map((raceClass, idx) => {
                                return (<>
                                    {/* <xmp>{JSON.stringify(countries)}</xmp> */}
                                    <RaceClassContestantList
                                        stores={this.props.stores}
                                        classNames={idx > 0 ? 'mt-5' : ''}
                                        title={`${raceClass.title} (${raceClassContestants.filter(rcc => rcc.raceClass === raceClass.id).length})`}
                                        raceClassContestants={raceClassContestants.filter(rcc => rcc.raceClass === raceClass.id)}
                                        // hideEmpty={}
                                        raceClass={raceClass}
                                        showCevron={false}
                                        stopTimer={this.stopTimer}
                                        drawerLevel={drawerLevel}
                                    />
                                </>);
                            })}
                        </> : <>
                            <div class={`d-flex flex-column mx-3 rounded-lg ${darkmode ? 'bg-darkmode' : 'bg-lightmode'} px-4 py-3`}>
                                <div class='w-100 d-flex flex-column px-4 py-3 box-line'>
                                    <div class='w-100 d-flex flex-row justify-content-center'>
                                        <div class='box-line-add'>
                                            <i class='fa-solid fa-circle-question' />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </>}
                    </>}

                    {/* Geofence States - Admin only */}
                    <GeofenceStateList stores={this.props.stores} />

                </div>
            </div>
        </>);
    }
}

export default RaceResults;
